import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Building,
  Target,
  Calendar,
  Download,
  RefreshCw,
  BarChart3,
  PieChart,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  Briefcase,
  Globe
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DetailedDashboard = () => {
  const { metricId } = useParams();
  const navigate = useNavigate();
  const [metricData, setMetricData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('6m');

  // Icon mapping
  const iconMap = {
    Activity,
    Target,
    Users,
    Building,
    TrendingUp,
    DollarSign,
    Briefcase,
    Globe
  };

  useEffect(() => {
    fetchMetricDetails();
  }, [metricId, timeRange]);

  const fetchMetricDetails = async () => {
    try {
      setLoading(true);
      // Directly use mock data without API call or delay
      const mockData = getMetricMockData(metricId);
      setMetricData(mockData);
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching metric details:', error);
      // Always fall back to mock data
      const mockData = getMetricMockData(metricId);
      setMetricData(mockData);
      setLoading(false);
    }
  };

  const getMetricMockData = (id) => {
    const mockData = {
      'child-nutrition-coverage': {
        title: 'Child Nutrition Coverage',
        currentValue: 92.4,
        unit: '%',
        trend: '+8.2%',
        icon: 'Activity',
        color: 'text-pink-500',
        description: 'Percentage of children under 6 receiving supplementary nutrition through Anganwadi centers',
        category: 'Child Development',
        targets: [
          { label: '2025 Target', value: 95, status: 'on-track' },
          { label: '2027 Goal', value: 98, status: 'planned' }
        ],
        kpis: [
          { label: 'Children Covered', value: '41.7L/45L', percentage: 92.4 },
          { label: 'Anganwadi Centers', value: '53,065', trend: '+5%' },
          { label: 'Take Home Ration', value: '89.2%', trend: '+12%' },
          { label: 'Nutrition Quality', value: '4.3/5', trend: '+0.4' }
        ],
        trends: [
          { month: 'Jan', value: 88.1 },
          { month: 'Feb', value: 89.3 },
          { month: 'Mar', value: 90.2 },
          { month: 'Apr', value: 91.1 },
          { month: 'May', value: 91.8 },
          { month: 'Jun', value: 92.4 }
        ],
        breakdown: [
          { district: 'Ahmedabad', centers: 3245, covered: 3098, percentage: 95.5 },
          { district: 'Surat', centers: 4123, covered: 3789, percentage: 91.9 },
          { district: 'Vadodara', centers: 3456, covered: 3201, percentage: 92.6 },
          { district: 'Rajkot', centers: 2876, covered: 2634, percentage: 91.6 },
          { district: 'Kutch', centers: 2567, covered: 2298, percentage: 89.5 },
          { district: 'Tribal Districts', centers: 13315, covered: 11982, percentage: 90.0 }
        ],
        insights: [
          'Take-Home Ration distribution improved by 12% this quarter',
          'Tribal areas showing significant improvement in nutrition coverage',
          'Face Recognition System reduced leakages by 85%',
          'Parent satisfaction with Anganwadi services increased to 4.3/5'
        ],
        recommendations: [
          'Expand coverage in tribal districts of Dahod and Chhota Udepur',
          'Implement AI-powered nutrition monitoring system',
          'Strengthen Take-Home Ration quality control',
          'Enhance mobile health worker training programs'
        ]
      },
      'anganwadi-digitization': {
        title: 'Anganwadi Digitization',
        currentValue: 78.3,
        unit: '%',
        trend: '+15.2%',
        icon: 'Target',
        color: 'text-blue-500',
        description: 'Progress in digitizing Anganwadi centers with modern facilities and technology',
        category: 'Child Development',
        targets: [
          { label: '2025 Target', value: 85, status: 'on-track' },
          { label: '2027 Goal', value: 100, status: 'planned' }
        ],
        kpis: [
          { label: 'Centers Digitized', value: '41,521/53,065', percentage: 78.3 },
          { label: 'Nandghar Centers', value: '15,234', trend: '+22%' },
          { label: 'Digital Equipment', value: '38,567', trend: '+18%' },
          { label: 'Staff Training', value: '89.4%', trend: '+12%' }
        ],
        trends: [
          { month: 'Jan', value: 71.2 },
          { month: 'Feb', value: 72.8 },
          { month: 'Mar', value: 74.5 },
          { month: 'Apr', value: 76.1 },
          { month: 'May', value: 77.4 },
          { month: 'Jun', value: 78.3 }
        ],
        breakdown: [
          { region: 'Central Gujarat', centers: 8967, digitized: 7345, percentage: 81.9 },
          { region: 'North Gujarat', centers: 9587, digitized: 7834, percentage: 81.7 },
          { region: 'Saurashtra', centers: 14532, digitized: 11245, percentage: 77.4 },
          { region: 'South Gujarat', centers: 12765, digitized: 9987, percentage: 78.2 },
          { region: 'East Gujarat', centers: 4567, digitized: 3234, percentage: 70.8 },
          { region: 'Kutch', centers: 2611, digitized: 1876, percentage: 71.8 }
        ],
        insights: [
          'Central and North Gujarat showing highest digitization rates',
          'Nandghar initiative accelerated digital infrastructure deployment',
          'Staff training programs show 89% completion rate',
          'LED TVs and tablets installation improving learning outcomes'
        ],
        recommendations: [
          'Prioritize digitization in East Gujarat and Kutch regions',
          'Accelerate Nandghar center construction in remote areas',
          'Establish inter-departmental coordination committees',
          'Use predictive analytics to identify potential bottlenecks'
        ]
      },
      'immunization-rate': {
        title: 'Immunization Rate',
        currentValue: 94.2,
        unit: '%',
        trend: '+6.1%',
        icon: 'Users',
        color: 'text-green-500',
        description: 'Percentage of children receiving complete immunization as per national schedule',
        category: 'Child Development',
        targets: [
          { label: '2025 Target', value: 95, status: 'on-track' },
          { label: '2027 Goal', value: 98, status: 'planned' }
        ],
        kpis: [
          { label: 'Full Immunization', value: '42.3L/45L', percentage: 94.2 },
          { label: 'BCG Coverage', value: '97.8%', trend: '+3%' },
          { label: 'DPT Coverage', value: '95.1%', trend: '+4%' },
          { label: 'Measles Coverage', value: '93.6%', trend: '+7%' }
        ],
        trends: [
          { month: 'Jan', value: 91.2 },
          { month: 'Feb', value: 92.1 },
          { month: 'Mar', value: 92.8 },
          { month: 'Apr', value: 93.4 },
          { month: 'May', value: 93.9 },
          { month: 'Jun', value: 94.2 }
        ],
        breakdown: [
          { district: 'Ahmedabad', target: 3200, achieved: 3098, percentage: 96.8 },
          { district: 'Surat', target: 4100, achieved: 3936, percentage: 96.0 },
          { district: 'Vadodara', target: 3400, achieved: 3179, percentage: 93.5 },
          { district: 'Rajkot', target: 2800, achieved: 2632, percentage: 94.0 },
          { district: 'Tribal Districts', target: 13000, achieved: 11830, percentage: 91.0 },
          { district: 'Other Districts', target: 15500, achieved: 14625, percentage: 94.4 }
        ],
        insights: [
          'BCG coverage showing excellent performance at 97.8%',
          'Tribal districts need targeted immunization drives',
          'Mobile immunization teams increased coverage by 7%',
          'Digital tracking system improved follow-up rates'
        ],
        recommendations: [
          'Strengthen immunization programs in tribal districts',
          'Expand mobile immunization van coverage',
          'Implement automated reminder systems for parents',
          'Create community awareness programs on vaccination'
        ]
      },
      'pre-school-enrollment': {
        title: 'Pre-School Enrollment',
        currentValue: 87.4,
        unit: '%',
        trend: '+12.1%',
        icon: 'Building',
        color: 'text-purple-500',
        description: 'Percentage of children aged 3-6 enrolled in pre-school education at Anganwadi centers',
        category: 'Child Development',
        targets: [
          { label: '2025 Target', value: 90, status: 'on-track' },
          { label: '2027 Goal', value: 95, status: 'planned' }
        ],
        kpis: [
          { label: 'Process Automation', value: '67.8%', percentage: 67.8 },
          { label: 'Document Processing', value: '2.1 days', trend: '-35%' },
          { label: 'Cost Reduction', value: '₹245 Cr', trend: '+28%' },
          { label: 'Staff Productivity', value: '84.7%', trend: '+15%' }
        ],
        trends: [
          { month: 'Jan', value: 78.2 },
          { month: 'Feb', value: 79.8 },
          { month: 'Mar', value: 81.5 },
          { month: 'Apr', value: 82.9 },
          { month: 'May', value: 83.8 },
          { month: 'Jun', value: 84.7 }
        ],
        breakdown: [
          { department: 'Revenue', efficiency: 89.2, automation: 78, cost_saving: 45 },
          { department: 'Municipal', efficiency: 85.4, automation: 67, cost_saving: 38 },
          { department: 'Transport', efficiency: 87.1, automation: 72, cost_saving: 42 },
          { department: 'Education', efficiency: 81.3, automation: 59, cost_saving: 31 },
          { department: 'Health', efficiency: 86.8, automation: 69, cost_saving: 39 },
          { department: 'Others', efficiency: 78.4, automation: 52, cost_saving: 28 }
        ],
        insights: [
          'Revenue department leading in automation adoption',
          'AI-powered document processing reduced time by 35%',
          'Cross-departmental workflow integration showing results',
          'Staff productivity gains directly correlate with automation'
        ],
        recommendations: [
          'Accelerate automation in Education and Others departments',
          'Implement AI-powered decision support systems',
          'Create shared service centers for common processes',
          'Develop predictive analytics for resource planning'
        ]
      },
      'vahli-dikri-beneficiaries': {
        title: 'Vahli Dikri Beneficiaries',
        currentValue: 2.3,
        unit: 'L',
        trend: '+19.2%',
        icon: 'TrendingUp',
        color: 'text-green-500',
        description: 'Number of girls benefiting from Vahli Dikri Yojana financial incentives for education',
        category: 'Women Empowerment',
        targets: [
          { label: '2025 Target', value: 2.8, status: 'on-track' },
          { label: '2027 Goal', value: 3.5, status: 'planned' }
        ],
        kpis: [
          { label: 'Total Beneficiaries', value: '2.3L', percentage: 82.1 },
          { label: 'Class 1 Enrollment', value: '₹4,000 x 95K', trend: '+15%' },
          { label: 'Class 9 Enrollment', value: '₹6,000 x 87K', trend: '+22%' },
          { label: 'Age 18 Benefits', value: '₹1L x 12K', trend: '+28%' }
        ],
        trends: [
          { month: 'Jan 2024', value: 1.8 },
          { month: 'Feb 2024', value: 1.9 },
          { month: 'Mar 2024', value: 2.0 },
          { month: 'Apr 2024', value: 2.1 },
          { month: 'May 2024', value: 2.2 },
          { month: 'Jun 2024', value: 2.3 }
        ],
        breakdown: [
          { district: 'Ahmedabad', enrolled: 28500, benefits: 15200, percentage: 53.3 },
          { district: 'Surat', enrolled: 32100, benefits: 19800, percentage: 61.7 },
          { district: 'Vadodara', enrolled: 21200, benefits: 12800, percentage: 60.4 },
          { district: 'Rajkot', enrolled: 18700, benefits: 11600, percentage: 62.0 },
          { district: 'Tribal Districts', enrolled: 45000, benefits: 24500, percentage: 54.4 },
          { district: 'Other Districts', enrolled: 84500, benefits: 46100, percentage: 54.6 }
        ],
        insights: [
          'Surat and Rajkot showing highest enrollment rates at 61-62%',
          'Age 18 benefit claims increased by 28% this year',
          'Rural areas seeing steady improvement in girl child enrollment',
          'Direct bank transfer system improved benefit delivery efficiency'
        ],
        recommendations: [
          'Increase awareness campaigns in tribal districts',
          'Strengthen school enrollment tracking systems',
          'Expand financial literacy programs for parents',
          'Create digital portals for easy benefit claim process'
        ]
      },
      'widow-pension-coverage': {
        title: 'Widow Pension Coverage',
        currentValue: 16.49,
        unit: 'L',
        trend: '+8.3%',
        icon: 'Users',
        color: 'text-blue-500',
        description: 'Number of widow women receiving monthly pension of ₹1,250 under Ganga Swarupa scheme',
        category: 'Women Empowerment',
        targets: [
          { label: '2025 Target', value: 18.5, status: 'on-track' },
          { label: '2027 Goal', value: 20.0, status: 'planned' }
        ],
        kpis: [
          { label: 'Total Beneficiaries', value: '16.49L', percentage: 89.2 },
          { label: 'Monthly Pension Amount', value: '₹1,250', trend: '+15%' },
          { label: 'Annual Budget', value: '₹3,015 Cr', trend: '+8%' },
          { label: 'Digital Disbursement', value: '94.7%', trend: '+12%' }
        ],
        trends: [
          { month: 'Jan', value: 15.2 },
          { month: 'Feb', value: 15.5 },
          { month: 'Mar', value: 15.8 },
          { month: 'Apr', value: 16.1 },
          { month: 'May', value: 16.3 },
          { month: 'Jun', value: 16.49 }
        ],
        breakdown: [
          { district: 'Ahmedabad', beneficiaries: 125000, disbursed: 118750, percentage: 95.0 },
          { district: 'Surat', beneficiaries: 98000, disbursed: 92820, percentage: 94.7 },
          { district: 'Vadodara', beneficiaries: 87000, disbursed: 82650, percentage: 95.0 },
          { district: 'Rajkot', beneficiaries: 76000, disbursed: 71440, percentage: 94.0 },
          { district: 'Kutch', beneficiaries: 45000, disbursed: 42300, percentage: 94.0 },
          { district: 'Other Districts', beneficiaries: 1218000, disbursed: 1144170, percentage: 93.9 }
        ],
        insights: [
          'Digital payment system ensures 94.7% electronic disbursement',
          'Monthly pension amount increased to ₹1,250 benefiting all widows',
          'Ahmedabad and Vadodara showing highest disbursement rates at 95%',
          'Real-time monitoring system reduced payment delays by 85%'
        ],
        recommendations: [
          'Expand awareness campaigns in rural areas for eligible widows',
          'Strengthen digital literacy programs for beneficiaries',
          'Implement biometric authentication to prevent fraud',
          'Create grievance redressal mechanism for quick issue resolution'
        ]
      },
      'violence-prevention-cases': {
        title: 'Violence Prevention Cases',
        currentValue: 91,
        unit: '%',
        trend: '+14.2%',
        icon: 'Activity',
        color: 'text-purple-500',
        description: 'Success rate in violence prevention and case resolution through Abhayam 181 and One Stop Centers',
        category: 'Women Empowerment',
        targets: [
          { label: '2025 Target', value: 95, status: 'on-track' },
          { label: '2027 Goal', value: 98, status: 'planned' }
        ],
        kpis: [
          { label: 'Cases Resolved', value: '12,450/13,681', percentage: 91.0 },
          { label: 'Abhayam 181 Calls', value: '45,234', trend: '+22%' },
          { label: 'One Stop Centers', value: '63 Centers', trend: '+8%' },
          { label: 'Response Time', value: '< 15 min', trend: '-25%' }
        ],
        trends: [
          { month: 'Jan', value: 85.2 },
          { month: 'Feb', value: 86.8 },
          { month: 'Mar', value: 87.9 },
          { month: 'Apr', value: 89.1 },
          { month: 'May', value: 90.2 },
          { month: 'Jun', value: 91.0 }
        ],
        breakdown: [
          { district: 'Ahmedabad', cases: 2850, resolved: 2622, percentage: 92.0 },
          { district: 'Surat', cases: 2340, resolved: 2129, percentage: 91.0 },
          { district: 'Vadodara', cases: 1890, resolved: 1719, percentage: 91.0 },
          { district: 'Rajkot', cases: 1670, resolved: 1503, percentage: 90.0 },
          { district: 'Tribal Districts', cases: 3456, resolved: 3041, percentage: 88.0 },
          { district: 'Other Districts', cases: 1475, resolved: 1321, percentage: 89.6 }
        ],
        insights: [
          'Abhayam 181 helpline showing 22% increase in call volume',
          'Average response time reduced to under 15 minutes',
          'One Stop Centers expanded to all 33 districts',
          'Digital case tracking system improved follow-up by 40%'
        ],
        recommendations: [
          'Increase awareness campaigns about 181 helpline services',
          'Strengthen counseling services at One Stop Centers',
          'Implement AI-powered threat assessment system',
          'Enhance coordination between police and support centers'
        ]
      },
      'women-skill-training': {
        title: 'Women Skill Training',
        currentValue: 1.2,
        unit: 'L',
        trend: '+23.4%',
        icon: 'Briefcase',
        color: 'text-pink-500',
        description: 'Women trained in various skills for economic empowerment and self-reliance',
        category: 'Women Empowerment',
        targets: [
          { label: '2025 Target', value: 1.5, status: 'on-track' },
          { label: '2027 Goal', value: 2.0, status: 'planned' }
        ],
        kpis: [
          { label: 'Women Trained', value: '1.2L', percentage: 80.0 },
          { label: 'Employment Rate', value: '78.5%', trend: '+18%' },
          { label: 'Skill Categories', value: '45 Skills', trend: '+12%' },
          { label: 'Certification Rate', value: '89.3%', trend: '+8%' }
        ],
        trends: [
          { month: 'Jan', value: 0.85 },
          { month: 'Feb', value: 0.92 },
          { month: 'Mar', value: 0.98 },
          { month: 'Apr', value: 1.04 },
          { month: 'May', value: 1.12 },
          { month: 'Jun', value: 1.20 }
        ],
        breakdown: [
          { skill: 'Tailoring & Handicrafts', trained: 35000, employed: 28700, percentage: 82.0 },
          { skill: 'Beauty & Wellness', trained: 28000, employed: 21840, percentage: 78.0 },
          { skill: 'Computer Skills', trained: 22000, employed: 17380, percentage: 79.0 },
          { skill: 'Food Processing', trained: 18000, employed: 14040, percentage: 78.0 },
          { skill: 'Digital Marketing', trained: 12000, employed: 9360, percentage: 78.0 },
          { skill: 'Other Skills', trained: 5000, employed: 3650, percentage: 73.0 }
        ],
        insights: [
          'Tailoring & Handicrafts showing highest employment rate at 82%',
          'Digital marketing skills gaining popularity among younger women',
          '78.5% of trained women found employment within 6 months',
          'Rural women participation increased by 35% through mobile training units'
        ],
        recommendations: [
          'Expand digital marketing and e-commerce training programs',
          'Create market linkage programs for skill-trained women',
          'Establish women entrepreneurship development centers',
          'Partner with industries for direct placement opportunities'
        ]
      },
      'employment-generation': {
        title: 'Employment Generation',
        currentValue: 2.4,
        unit: 'L Jobs',
        trend: '+21.3%',
        icon: 'Briefcase',
        color: 'text-purple-500',
        description: 'New employment opportunities created across Maharashtra',
        category: 'Economic Growth',
        targets: [
          { label: '2025 Target', value: 3.0, status: 'on-track' },
          { label: '2027 Goal', value: 4.5, status: 'planned' }
        ],
        kpis: [
          { label: 'Jobs Created', value: '2.4L', percentage: 80.0 },
          { label: 'Skill Training', value: '1.8L', trend: '+35%' },
          { label: 'Women Participation', value: '42.3%', trend: '+8%' },
          { label: 'Rural Employment', value: '38.7%', trend: '+15%' }
        ],
        trends: [
          { month: 'Jan', value: 1.8 },
          { month: 'Feb', value: 1.9 },
          { month: 'Mar', value: 2.0 },
          { month: 'Apr', value: 2.1 },
          { month: 'May', value: 2.2 },
          { month: 'Jun', value: 2.4 }
        ],
        breakdown: [
          { sector: 'Information Technology', jobs: 85000, skilled: 95, women: 48, rural: 15 },
          { sector: 'Manufacturing', jobs: 67000, skilled: 78, women: 35, rural: 45 },
          { sector: 'Services', jobs: 54000, skilled: 82, women: 52, rural: 25 },
          { sector: 'Agriculture', jobs: 23000, skilled: 45, women: 38, rural: 85 },
          { sector: 'Construction', jobs: 11000, skilled: 65, women: 22, rural: 65 }
        ],
        insights: [
          'IT sector contributing 35% of total new employment',
          'Women participation increased to 42.3%',
          'Rural employment share growing significantly',
          'Skill training programs covering 75% of new jobs'
        ],
        recommendations: [
          'Expand skill development programs in emerging technologies',
          'Create women-focused employment initiatives',
          'Develop rural entrepreneurship support systems',
          'Establish industry-academia partnerships for training'
        ]
      },
      'digital-economy-share': {
        title: 'Digital Economy Share',
        currentValue: 29.2,
        unit: '%',
        trend: '+12.8%',
        icon: 'Globe',
        color: 'text-orange-500',
        description: 'Percentage of state GDP contributed by digital economy sectors',
        category: 'Economic Growth',
        targets: [
          { label: '2025 Target', value: 35, status: 'on-track' },
          { label: '2027 Goal', value: 45, status: 'planned' }
        ],
        kpis: [
          { label: 'Digital GDP', value: '₹9.5L Cr', percentage: 29.2 },
          { label: 'Digital Exports', value: '₹3.2L Cr', trend: '+28%' },
          { label: 'Startups Registered', value: '12,450', trend: '+45%' },
          { label: 'Digital Workforce', value: '18.7L', trend: '+22%' }
        ],
        trends: [
          { month: 'Jan', value: 26.8 },
          { month: 'Feb', value: 27.2 },
          { month: 'Mar', value: 27.8 },
          { month: 'Apr', value: 28.4 },
          { month: 'May', value: 28.8 },
          { month: 'Jun', value: 29.2 }
        ],
        breakdown: [
          { segment: 'IT Services', share: 8.5, growth: 15.2, value: 2.75 },
          { segment: 'E-commerce', share: 6.8, growth: 22.1, value: 2.20 },
          { segment: 'Fintech', share: 4.3, growth: 35.6, value: 1.39 },
          { segment: 'Digital Media', share: 3.9, growth: 18.7, value: 1.26 },
          { segment: 'EdTech', share: 2.8, growth: 42.3, value: 0.91 },
          { segment: 'HealthTech', share: 2.9, growth: 28.9, value: 0.94 }
        ],
        insights: [
          'IT Services leading digital economy at 8.5% of GDP',
          'EdTech showing highest growth rate at 42.3%',
          'Digital workforce reached 18.7 lakh people',
          'Startup registrations increased by 45%'
        ],
        recommendations: [
          'Focus on emerging tech sectors like AI and blockchain',
          'Create digital innovation districts in major cities',
          'Strengthen digital infrastructure in tier-2/3 cities',
          'Develop comprehensive digital skills training programs'
        ]
      }
    };

    return mockData[id] || mockData['digital-service-delivery'];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on-track': return 'bg-green-100 text-green-700';
      case 'at-risk': return 'bg-yellow-100 text-yellow-700';
      case 'delayed': return 'bg-red-100 text-red-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  const formatValue = (value, unit) => {
    if (unit === '₹L Cr') {
      return `₹${value}L Cr`;
    } else if (unit === 'L Jobs') {
      return `${value}L Jobs`;
    }
    return `${value}${unit}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading detailed dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!metricData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Dashboard Not Found</h1>
            <p className="text-gray-600 mb-8">The requested dashboard could not be found.</p>
            <Button onClick={() => navigate('/')}>Return to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Button>
            <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30">
              {metricData.category}
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center mb-4">
                <div className="text-6xl mr-4">
                  {metricData?.icon && iconMap[metricData.icon] ? 
                    React.createElement(iconMap[metricData.icon], { 
                      className: `w-16 h-16 ${metricData.color}` 
                    }) : 
                    <Activity className={`w-16 h-16 ${metricData?.color || 'text-orange-500'}`} />
                  }
                </div>
                <div>
                  <h1 className="text-4xl font-bold mb-2">{metricData?.title || 'Loading...'}</h1>
                  <p className="text-xl text-blue-100">{metricData?.description || 'Loading dashboard data...'}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-6 mb-6">
                <div className="text-5xl font-bold">
                  {metricData ? formatValue(metricData.currentValue, metricData.unit) : 'Loading...'}
                </div>
                <Badge className="bg-green-100 text-green-700 text-lg px-3 py-1">
                  {metricData?.trend && metricData.trend.startsWith('+') ? (
                    <TrendingUp className="w-4 h-4 mr-1" />
                  ) : (
                    <TrendingDown className="w-4 h-4 mr-1" />
                  )}
                  {metricData?.trend || '+0%'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {metricData?.targets?.map((target, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-300">{target.label}</span>
                      <Badge className={getStatusColor(target.status)}>
                        {target.status}
                      </Badge>
                    </div>
                    <div className="text-2xl font-bold mt-2">
                      {metricData ? formatValue(target.value, metricData.unit) : 'Loading...'}
                    </div>
                  </div>
                )) || (
                  <div className="col-span-2 text-center text-gray-300">
                    Loading targets...
                  </div>
                )}
              </div>
            </div>
            
            {/* Time Range Selector */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-lg font-semibold mb-4">Time Range</h3>
              <div className="space-y-2">
                {['1m', '3m', '6m', '1y', '2y'].map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      timeRange === range
                        ? 'bg-orange-500 text-white'
                        : 'bg-white/10 hover:bg-white/20 text-white'
                    }`}
                  >
                    {range === '1m' && 'Last Month'}
                    {range === '3m' && 'Last 3 Months'}
                    {range === '6m' && 'Last 6 Months'}
                    {range === '1y' && 'Last Year'}
                    {range === '2y' && 'Last 2 Years'}
                  </button>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/20">
                <Button 
                  onClick={fetchMetricDetails}
                  className="w-full bg-white/20 hover:bg-white/30 text-white"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Data
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5 bg-white shadow-sm p-1 rounded-xl mb-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="trends">Trends</TabsTrigger>
              <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
              <TabsTrigger value="insights">Insights</TabsTrigger>
              <TabsTrigger value="actions">Actions</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-8">
              {metricData?.kpis && metricData.kpis.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {metricData.kpis.map((kpi, index) => (
                    <Card key={index}>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">
                          {kpi.label}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-gray-900 mb-2">
                          {kpi.value}
                        </div>
                        {kpi.percentage && (
                          <Progress value={kpi.percentage} className="h-2 mb-2" />
                        )}
                        {kpi.trend && (
                          <Badge 
                            variant="secondary"
                            className={`text-xs ${
                              kpi.trend.startsWith('+') 
                                ? 'bg-green-100 text-green-700' 
                                : kpi.trend.startsWith('-') && !kpi.trend.includes('days') && !kpi.trend.includes('hrs')
                                ? 'bg-red-100 text-red-700'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {kpi.trend}
                          </Badge>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading overview data...</p>
                </div>
              )}
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2 text-orange-500" />
                    Trend Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {metricData?.trends && metricData.trends.length > 0 ? (
                    <div className="space-y-4">
                      {metricData.trends.map((trend, index) => {
                        const maxValue = Math.max(...metricData.trends.map(t => t.value));
                        const progressWidth = maxValue > 0 ? (trend.value / maxValue) * 100 : 0;
                        
                        return (
                          <div key={index} className="flex items-center justify-between py-3 border-b last:border-b-0">
                            <span className="font-medium text-gray-700">{trend.month}</span>
                            <div className="flex items-center space-x-4">
                              <div className="w-64 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-orange-500 h-2 rounded-full"
                                  style={{ width: `${progressWidth}%` }}
                                />
                              </div>
                              <span className="font-bold text-lg min-w-[80px] text-right">
                                {formatValue(trend.value, metricData.unit)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Loading trend data...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Breakdown Tab */}
            <TabsContent value="breakdown">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <PieChart className="w-5 h-5 mr-2 text-orange-500" />
                    Detailed Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {metricData?.breakdown && metricData.breakdown.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 font-semibold text-gray-900">
                              {Object.keys(metricData.breakdown[0])[0]?.charAt(0).toUpperCase() + Object.keys(metricData.breakdown[0])[0]?.slice(1)}
                            </th>
                            {Object.keys(metricData.breakdown[0]).slice(1).map((key) => (
                              <th key={key} className="text-right py-3 font-semibold text-gray-900">
                                {key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {metricData.breakdown.map((item, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50">
                              <td className="py-3 font-medium text-gray-900">
                                {Object.values(item)[0]}
                              </td>
                              {Object.entries(item).slice(1).map(([key, value]) => (
                                <td key={key} className="py-3 text-right text-gray-600">
                                  {typeof value === 'number' && value < 100 && key.includes('percentage') 
                                    ? `${value}%` 
                                    : value}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Loading breakdown data...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                      Key Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {metricData?.insights && metricData.insights.length > 0 ? (
                      <ul className="space-y-4">
                        {metricData.insights.map((insight, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{insight}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500">Loading insights...</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2 text-orange-500" />
                      Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {metricData?.recommendations && metricData.recommendations.length > 0 ? (
                      <ul className="space-y-4">
                        {metricData.recommendations.map((recommendation, index) => (
                          <li key={index} className="flex items-start">
                            <AlertCircle className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{recommendation}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500">Loading recommendations...</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Actions Tab */}
            <TabsContent value="actions">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle>Export Data</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button className="w-full" variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF Report
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Export to Excel
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Get API Access
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Set Alerts</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button className="w-full" variant="outline">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Target Achievement
                    </Button>
                    <Button className="w-full" variant="outline">
                      <TrendingDown className="w-4 h-4 mr-2" />
                      Performance Drop
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Calendar className="w-4 h-4 mr-2" />
                      Scheduled Reports
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Share Dashboard</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button className="w-full" variant="outline">
                      <Users className="w-4 h-4 mr-2" />
                      Share with Team
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Globe className="w-4 h-4 mr-2" />
                      Public Dashboard
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Clock className="w-4 h-4 mr-2" />
                      Schedule Presentation
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DetailedDashboard;